import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { sortTasksByPosition } from '@/utils/fractional-ordering'

import type { TaskWithAssignments } from '@scrambl/shared'
import { isOlderRecordRevision } from '@scrambl/shared/offline'

import type { TaskReaderProjection } from './task-reader-projection'
import {
  pruneTaskReaderFields,
  taskReaderProjection,
} from './task-reader-projection'

type TaskRecord = Record<string, unknown>

const stripUndefinedValues = (value: TaskRecord): TaskRecord => {
  const next: TaskRecord = {}
  Object.entries(value).forEach(([key, itemValue]) => {
    if (itemValue !== undefined) {
      next[key] = itemValue
    }
  })
  return next
}

const preserveAvailableStatuses = (
  cachedTask: TaskRecord,
  updates: TaskRecord,
): TaskRecord => {
  if (
    (!Object.prototype.hasOwnProperty.call(updates, 'availableStatuses') ||
      updates.availableStatuses == null) &&
    Object.prototype.hasOwnProperty.call(cachedTask, 'availableStatuses')
  ) {
    return {
      ...updates,
      availableStatuses: cachedTask.availableStatuses,
    }
  }

  return updates
}

const mergeTaskPatch = (
  cachedTask: TaskRecord,
  patch: TaskRecord,
): TaskRecord => {
  if (isOlderRecordRevision(cachedTask.revision, patch.revision))
    return cachedTask
  const incomingProjection = taskReaderProjection(patch)
  const cachedProjection = taskReaderProjection(cachedTask)
  if (
    Object.hasOwn(patch, '_readerProjection') &&
    (!incomingProjection ||
      (cachedProjection &&
        isOlderRecordRevision(
          cachedProjection.revision,
          incomingProjection.revision,
        )))
  )
    return cachedTask
  const sanitizedPatch = stripUndefinedValues(patch)
  const mergedPatch = preserveAvailableStatuses(cachedTask, sanitizedPatch)
  const nextPatch = Object.prototype.hasOwnProperty.call(mergedPatch, 'groupId')
    ? {
        ...mergedPatch,
        group:
          mergedPatch.group &&
          (mergedPatch.group as TaskRecord).id === mergedPatch.groupId
            ? mergedPatch.group
            : cachedTask.group &&
                (cachedTask.group as TaskRecord).id === mergedPatch.groupId
              ? cachedTask.group
              : null,
      }
    : mergedPatch

  const merged = { ...cachedTask, ...nextPatch }
  const projection = incomingProjection ?? cachedProjection
  return projection ? pruneTaskReaderFields(merged, projection) : merged
}

type UpdateOperation = (qc: QueryClient) => void
type StoreOperation = () => void

/** Preserve page/container/sibling identity unless an existing task changes. */
function patchTaskCacheData(
  data: unknown,
  taskId: string,
  patch: TaskRecord,
  sortSubtasks: boolean,
  transform: (
    row: TaskRecord,
    patch: TaskRecord,
  ) => TaskRecord = mergeTaskPatch,
): unknown {
  if (!data || typeof data !== 'object') return data
  if (Array.isArray(data)) {
    let changed = false
    const next = data.map(value => {
      const updated = patchTaskCacheData(
        value,
        taskId,
        patch,
        sortSubtasks,
        transform,
      )
      if (updated !== value) changed = true
      return updated
    })
    if (!changed) return data
    return sortSubtasks &&
      next.every(value => value && typeof value === 'object' && 'id' in value)
      ? sortTasksByPosition(next as TaskWithAssignments[])
      : next
  }
  const record = data as TaskRecord
  if (record.id === taskId) return transform(record, patch)
  let result = record
  for (const key of ['tasks', 'data', 'pages']) {
    if (!Array.isArray(record[key])) continue
    const next = patchTaskCacheData(
      record[key],
      taskId,
      patch,
      sortSubtasks,
      transform,
    )
    if (next !== record[key]) result = { ...result, [key]: next }
  }
  return result
}

/** Missing fields in a realtime patch do not change list membership. */
export function changedTaskPatchKeys<T extends object>(
  current: T,
  patch: Partial<T>,
): Array<keyof T> {
  return (Object.keys(patch) as Array<keyof T>).filter(key => {
    const before = current[key]
    const after = patch[key]
    if (after === undefined) return false
    return before instanceof Date || after instanceof Date
      ? before && after
        ? String(before) !== String(after)
        : before !== after
      : JSON.stringify(before) !== JSON.stringify(after)
  })
}

export class RealtimeCacheUpdater {
  private queryClient: QueryClient
  private queue: UpdateOperation[] = []
  private storeQueue: StoreOperation[] = []
  private scheduled = false
  private batchMs: number
  private canApply: () => boolean

  constructor(
    queryClient: QueryClient,
    batchMs: number = 120,
    canApply: () => boolean = () => true,
  ) {
    this.queryClient = queryClient
    this.batchMs = batchMs
    this.canApply = canApply
  }

  schedule(op: UpdateOperation) {
    this.queue.push(op)
    if (!this.scheduled) {
      this.scheduled = true
      window.setTimeout(() => this.flush(), this.batchMs)
    }
  }

  private flush() {
    const ops = this.queue
    const storeOps = this.storeQueue
    this.queue = []
    this.storeQueue = []
    this.scheduled = false
    if (ops.length === 0) return
    // Execute ops in a single frame to encourage React 18 batching
    const run = () => {
      for (const op of ops) {
        if (!this.canApply()) return
        op(this.queryClient)
      }
      for (const sop of storeOps) {
        if (!this.canApply()) return
        sop()
      }
    }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run)
    } else {
      run()
    }
  }

  // Common helpers
  mergeIntoArrayCache<T extends { id: string }>(key: QueryKey, item: T) {
    this.schedule(qc => {
      qc.setQueryData<T[] | undefined>(key, old => {
        const list = old || []
        const idx = list.findIndex(t => t.id === item.id)
        if (idx !== -1) {
          const merged = mergeTaskPatch(
            list[idx] as TaskRecord,
            item as TaskRecord,
          ) as T
          if (merged === list[idx]) return old
          const copy = list.slice()
          copy[idx] = merged
          return copy
        }
        // Replace an optimistic temp entry if present
        const tempIdx = list.findIndex(
          t => typeof t.id === 'string' && t.id.startsWith('temp-'),
        )
        if (tempIdx !== -1) {
          const copy = list.slice()
          copy[tempIdx] = item
          return copy
        }
        return [...list, item]
      })
    })
  }

  /**
   * Update task fields in cache. Uses targeted approach - only calls setQueryData
   * on queries that ACTUALLY contain the task, avoiding any iteration over unrelated queries.
   */
  updateTaskFields<T extends { id: string }>(
    taskId: string,
    partial: Partial<T>,
  ) {
    this.schedule(qc => {
      // Update single task detail cache if it exists
      const detailKey = ['task', taskId]
      const detailData = qc.getQueryData<any>(detailKey)
      if (detailData && detailData.id === taskId) {
        const merged = mergeTaskPatch(detailData as TaskRecord, {
          ...(partial as TaskRecord),
          _realtime_updated: Date.now(),
        })
        if (merged !== detailData) qc.setQueryData(detailKey, merged)
      }

      // Update task detail caches keyed by queryKeys.tasks.detail/byNumber
      const detailQueries = qc.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'tasks' &&
          (q.queryKey[1] === 'detail' || q.queryKey[1] === 'by-number'),
      })

      for (const [queryKey, oldData] of detailQueries) {
        if (!oldData || oldData.id !== taskId) continue
        const merged = mergeTaskPatch(oldData as TaskRecord, {
          ...(partial as TaskRecord),
          _realtime_updated: Date.now(),
        })
        if (merged !== oldData) qc.setQueryData(queryKey, merged)
      }

      // Includes subtask copies and paginated caches without inserting a row
      // into a list whose filters/membership are known only by its reader.
      const allQueries = qc.getQueriesData<unknown>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          ['tasks', 'my-tasks', 'subtasks', 'project-subtask-index'].includes(
            String(q.queryKey[0]),
          ) &&
          !(
            q.queryKey[0] === 'tasks' &&
            ['detail', 'by-number'].includes(String(q.queryKey[1]))
          ),
      })

      for (const [queryKey, oldData] of allQueries) {
        const next = patchTaskCacheData(
          oldData,
          taskId,
          {
            ...(partial as TaskRecord),
            _realtime_updated: Date.now(),
          },
          queryKey[0] === 'subtasks',
        )
        if (next !== oldData) qc.setQueryData(queryKey, next)
      }
    })
  }

  /** Field retirement is immediate; queued patches still consult the cached mask. */
  redactTaskFields(taskId: string, projection: TaskReaderProjection) {
    if (!this.canApply()) return
    const queries = this.queryClient.getQueriesData<unknown>({
      predicate: query =>
        [
          'task',
          'tasks',
          'my-tasks',
          'subtasks',
          'project-subtask-index',
        ].includes(String(query.queryKey[0])),
    })
    for (const [key, data] of queries) {
      const next = patchTaskCacheData(data, taskId, {}, false, row => {
        if (
          isOlderRecordRevision(row.revision, projection.revision) ||
          isOlderRecordRevision(
            taskReaderProjection(row)?.revision,
            projection.revision,
          )
        )
          return row
        return pruneTaskReaderFields(row, projection)
      })
      if (next !== data) this.queryClient.setQueryData(key, next)
    }
  }

  /**
   * Remove a task from main task lists. Uses targeted approach - only calls setQueryData
   * on queries that ACTUALLY contain the task.
   */
  removeTaskFromMainLists(taskId: string) {
    this.schedule(qc => {
      // Find and update ONLY queries that contain this task
      // Include both 'tasks' and 'my-tasks' queries
      const allQueries = qc.getQueriesData<any>({
        predicate: q =>
          Array.isArray(q.queryKey) &&
          (q.queryKey[0] === 'tasks' || q.queryKey[0] === 'my-tasks'),
      })

      for (const [queryKey, oldData] of allQueries) {
        if (!oldData) continue

        const taskArray = Array.isArray(oldData)
          ? oldData
          : oldData.tasks || oldData.data
        if (!Array.isArray(taskArray)) continue

        const idx = taskArray.findIndex((t: any) => t.id === taskId)
        if (idx === -1) continue // Task not in this query - skip entirely

        // Only update this specific query
        const filtered = taskArray.filter((t: any) => t.id !== taskId)

        if (Array.isArray(oldData)) {
          qc.setQueryData(queryKey, filtered)
        } else if (oldData.tasks) {
          qc.setQueryData(queryKey, { ...oldData, tasks: filtered })
        } else if (oldData.data) {
          qc.setQueryData(queryKey, { ...oldData, data: filtered })
        }
      }
    })
  }

  invalidate(
    key: QueryKey,
    refetchType: 'active' | 'inactive' | 'all' | 'none' = 'none',
  ) {
    this.schedule(qc => {
      qc.invalidateQueries({ queryKey: key as any, refetchType })
    })
  }

  // Zustand/store updates can also be coalesced
  scheduleStore(op: StoreOperation) {
    this.storeQueue.push(op)
    if (!this.scheduled) {
      this.scheduled = true
      window.setTimeout(() => this.flush(), this.batchMs)
    }
  }
}

export function createRealtimeCacheUpdater(
  queryClient: QueryClient,
  batchMs?: number,
  canApply?: () => boolean,
) {
  return new RealtimeCacheUpdater(queryClient, batchMs, canApply)
}
